import React, { useContext, useEffect, useState } from "react";
import { UserContext } from "../../context/user.context";
import OrderDetails from "./OrderDetails";
import { UilSync } from "@iconscout/react-unicons";
import { fetchOrdersHistory } from "../../utils/SonicAPIV2";
import Pagination from "../../components/Pagination";
import { toast } from "react-toastify";

export const Orders = () => {
  const { currentUser } = useContext(UserContext);

  // Orders state
  const [orders, setOrders] = useState([]);
  const [ordersCount, setOrdersCount] = useState(0);

  // Paging state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const totalOrders = orders.length;

  // UI state
  const [orderInfo, setOrderInfo] = useState(null);
  const [showPopUp, setShowPopUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all orders on mount or on refresh
  useEffect(() => {
    LoadData();
    // eslint-disable-next-line
  }, []);

  async function LoadData() {
    setLoading(true);
    setError(null);
    try {
      // Fetch all orders (limit high enough for all)
      const data = await fetchOrdersHistory(false, 1000);
      //console.log("Orders data from Orders.jsx:", data);

      const list = data.orderList || [];
      setOrders(list);
      setOrdersCount(data.ordersCount || list.length);
      setPage(1);
    } catch (err) {
      setError(err.message || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  }

  // Paging logic
  const totalPages = Math.max(1, Math.ceil(ordersCount / pageSize));
  const pagedOrders = orders.slice((page - 1) * pageSize, page * pageSize);

  function showPopUpDialog(orderNumber) {
    // console.log("showPopUpDialog called with orderNumber:", orderNumber);
    // console.log(orders);

    const orderDetails = orders.find(
      (order) => order.orderNumber === orderNumber
    );

    if (!orderDetails) {
      //console.error("Order not found:", orderNumber);
      toast.error("Order details not found");
      return;
    }

    setOrderInfo([orderDetails]);
    setShowPopUp(true);
  }

  function formatOrderNumber(orderNumber) {
    if (orderNumber?.length === 36) {
      return orderNumber.slice(-6).toUpperCase();
    }
    return orderNumber;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center">
        <div className="text-navarbarText text-3xl font-poppins font-medium mb-8">
          Orders
          <p className="text-base text-darkTextColor font-poppins font-normal">
            Last 6 Months orders with details and status.
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            className="flex rounded-md bg-secondary h-10 w-10 items-center justify-center text-center"
            onClick={LoadData}
            disabled={loading}
          >
            <UilSync className="w-6 text-textWhite" />
          </button>
        </div>
      </div>

      <div className="-mx-4 mt-8 sm:-mx-0">
        <table className="min-w-full divide-y divide-gray-300">
          <thead>
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">
                Order #
              </th>
              <th className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell">
                Order Date
              </th>
              <th className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell">
                Schedule Date
              </th>
              <th className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">
                Total
              </th>
              <th className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell">
                Status
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Order Details
              </th>
              <th className="relative py-3.5 pl-3 pr-4 sm:pr-0">
                <span className="sr-only">Details</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? (
              <tr>
                <td colSpan="7" className="py-5 text-center">
                  Loading...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="7" className="py-5 text-center text-red-600">
                  {error}
                </td>
              </tr>
            ) : pagedOrders.length > 0 ? (
              pagedOrders.map((item) => (
                <tr key={item.orderNumber}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0">
                    {formatOrderNumber(item.orderNumber)}
                  </td>
                  <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell">
                    {item.orderDate}
                  </td>
                  <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell">
                    {item.scheduleDate}
                  </td>
                  <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 lg:table-cell">
                    {Number(item.netTotal ?? 0).toLocaleString("en-AE", {
                      style: "currency",
                      currency: "AED",
                      minimumFractionDigits: 2,
                    })}
                  </td>
                  <td className="hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 lg:table-cell">
                    {item.status}
                  </td>
                  <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                    <button
                      onClick={() => showPopUpDialog(item.orderNumber)}
                      className="flex bg-secondary h-8 rounded-md w-16 text-textWhite justify-center text-center items-center text-base font-poppins hover:bg-secondaryHover"
                    >
                      Details
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="py-5 text-center">
                  No Orders to Display
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <OrderDetails
          showPopUp={showPopUp}
          orderInfo={orderInfo}
          onClose={() => setShowPopUp(false)}
        />

        <Pagination
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalCount={totalOrders}
        />
      </div>
    </div>
  );
};
